import React, { useEffect, useState, useMemo, Fragment, useCallback, useRef } from 'react';
import BrandLoader from '../shared/brand/BrandLoader';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, RootState } from '../../store';
import { useSelector } from 'react-redux';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import {
  PiFileTextLight,
  PiCheckCircleLight,
  PiDotsThreeVerticalLight,
  PiNoteLight,
  PiQuestionMarkLight,
  PiFileLight,
  PiUsersLight,
  PiCheckSquareOffsetLight,
  PiLightningLight,
  PiWarningLight,
  PiClockCounterClockwiseLight,
  PiChartLineUpLight,
} from 'react-icons/pi';
import { getUserType, isBetaEnv } from '../../utils/authUtils';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { addMatterCollaborators, removeMatterCollaborator, updateCollaboratorAccess } from '../../store/actions/Matters/MatterActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import { Action } from '../shared/TailwindComponents/Header';
import DetailFrame from '../shared/Frames/DetailFrame';
import { isMatterEconomicsEnabled } from '../../utils/config';
import { PiPencilSimpleLight } from 'react-icons/pi';
import type { PricingModel } from '../../types/economics';
import MatterDetailsContent from './MatterDetailsContent';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import DetailTabBar, { TabItem } from '../shared/surface/DetailTabBar';
import { fetchMatterSources, fetchQuestionnaire, fetchMatterNotes, archiveMatter, editMatter, clearPrefetchedMatterData, fetchMatterDetails, fetchSuggestedTasks, updateSuggestedTasks, analyzeMatter, getAnalysisStatus, addFileToMatter } from '../../store/actions/Matters/MatterActions';
import { fetchAllTasks } from '../../store/actions/TasksActions';
import { fetchDataSources } from '../../store/actions/AIStudioAction';
import ProgressTracker, { Step } from './ProgressTracker';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import AnalyzeWizardModal, { AnalyzeWizardResult } from './AnalyzeWizardModal';
import { FeatureTour, matterDetailOrientationSteps } from '../shared/FeatureTour';
import { useFeatureTour, clearActiveTourLock } from '../../hooks/useFeatureTour';
import ProjectBadges from '../Projects/ProjectBadges';
import MarketplaceOriginBadge from '../Marketplace/MarketplaceOriginBadge';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import DetailPageSurface from '../shared/surface/DetailPageSurface';
import { MATTER_SURFACE, MATTER_TABS, MatterTab, useMatterWorkspace } from './surface';
import { MatterSurfaceProvider } from './surface/MatterSurfaceContext';
import MatterContextPane from './surface/MatterContextPane';
import MatterConnectedPane from './surface/MatterConnectedPane';
import { useConnectedWork } from './surface/useConnectedWork';
import MatterActivityPane from './surface/MatterActivityPane';
import AIUsagePanel from '../shared/aiUsage/AIUsagePanel';
import MatterReaderPane from './surface/MatterReaderPane';
import MatterAssistantPane from './surface/MatterAssistantPane';
import MatterItemDetail from './surface/detail/MatterItemDetail';
import { suggestionId } from './tasks/taskConstants';
import AccessPane from '../shared/access/AccessPane';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import PaneRail from '../shared/surface/PaneRail';

import { fetchMatterDropdownStatuses } from '../../store/actions/Matters/MatterActions';
import apis from '../../utils/apis';
import {
  filenameFromContentDisposition,
  saveBlob,
  withExtension,
} from '../../utils/download';
import {
  PiBookOpenLight,
  PiChatsCircleLight,
  PiSquaresFourLight,
  PiFolderSimplePlusLight,
  PiArchiveLight,
  PiDownloadSimpleLight,
  PiListMagnifyingGlassLight,
  PiInfoLight,
  PiSparkleLight,
} from 'react-icons/pi';
import Spinner from '../shared/TailwindComponents/Spinner';
import { toErrorText } from '../../utils/errorMessage';

export interface MatterData {
  id: string;
  uuid: string;
  MatterID: string;
  MatterName: string;
  MatterType: string;
  UserID: string;
  status: string;
  Description: string;
  Notes: string; 
  fields: {
    bulk_files: Array<{
      label: string;
      content: string;
      source_id: string;
    }>;
    Language: string;
    FetchApplicableLaws: string;
    AI_Description: string;
  };
  created_at: string;
  updated_at?: string;
  DateOpened: string;
  DateClosed: null | string;
  Jurisdiction: string;
  jurisdiction: string;
  language?: string;
  Language?: string;
  due_date?: string | null;
  DueDate?: string | null;
  questionnaire: Array<{
    question: string;
    potential_answer: string;
    who_answered: string;
    when_answered: string;
    status: 'pending';
    client_assigned: boolean;
  }>;
  MatterStage: string;
  Clients: Array<{
    ClientID: string;
    ClientName: string;
    ClientType: string;
    Roles: string[];
    IDVerificationStatus: string;
    ContactDetails: Record<string, any>;
    UserID: string | null;
  }>;
  OpposingParties: any[];
  Attorneys: any[];
  Collaborators: Array<{
    UserID: number;
    FullName: string;
    Email: string;
    Permission: 'write';
    SharedBy: string;
    SharedAt: string;
    Role: string;
  }>;
  BillingStatus: string;
  // Matter Economics (tag matter-economics) - snake_case per MATTER_ECONOMICS_API.md.
  pricing_model?: PricingModel | null;
  currency?: string | null;
  fixed_fee_amount?: number | null;
  /** Budget / fee cap, on any pricing model. Null when none is set. */
  budget_amount?: number | null;
  contingency_pct?: number | null;
  InitialTasks: any[];
  Processes: any[];
  RetrievedLaws: {
    response: string;
    citations: any[];
  };
  RetrievedCases: {
    response: string;
    citations: any[];
  };
  is_analysis_run: boolean;
  FinancialData: Array<{
    label: string;
    value: string;
    type: 'short_text';
  }>;
  is_deleted: boolean;
  processing_status?: string;
  processing_correlation_id?: string;
  last_analyzed_at?: string;
  MatterHistory: Array<{
    action: string;
    performed_by: string;
    details: string;
    timestamp: string;
  }>;
  LawyerID: string;
  LawyerName: string;
  OrganizationID: string;
  OrganizationName: string;
  MatterFolderID: string;
  current_stage_id?: number;
  current_status_id?: number;
  category_id?: number;
  category_name?: string;
  category_description?: string;
  stage_id?: number;
  stage_name?: string;
  status_id?: number;
  status_name?: string;
  collaborators?: Array<{
    access: string;
    avatar_url: string | null;
    collaborator_id: string;
    email: string;
    initials: string | null;
    matter_id: string;
    name: string;
    role: string;
    shared_at: string;
    shared_by: string;
    shared_by_user?: {
      email: string;
      first_name: string;
      full_name: string;
      last_name: string;
      user_id: number;
      uuid: string;
    };
  }>;
  matter_clients?: {
    clients: Array<{
      added_at: string;
      added_by_user_id: number;
      client_id: number;
      client_uuid: string;
      contact_phone: string;
      email: string;
      name: string;
      role: string;
      role_id: string;
      role_name: string;
    }>;
    count: number;
    created_at: string;
    created_by_user_id: number;
    matter_uuid: string;
  };
}

interface RouteParams {
  matterId: string;
}


interface DataState {
  data: MatterData | null;
  lastFetched: number | null;
  isStale: boolean;
  error: string | null;
  hasTriedLoading: boolean;
}

const buildBreadcrumbs = (
  t: (key: string) => string,
  matterName: string | undefined,
  matterId: string,
  activeTab: string,
) => [
  { name: t('Matters'), href: '/matters', current: false },
  { name: matterName || t('Loading...'), href: `/matters/${matterId}`, current: false },
  { name: t(activeTab), href: `/matters/${matterId}?tab=${activeTab}`, current: true },
];

const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`animate-fadePulse bg-gray-200 dark:bg-gray-700 ${className}`}></div>
);

export const getStatusColor = (status: string | undefined | null): string => {
  if (!status || typeof status !== 'string' || status.trim() === '') {
    return 'gray';
  }

  const normalizedStatus = status.trim().toLowerCase();
  
  switch (normalizedStatus) {
    case 'draft': return 'gray';
    case 'active': 
    case 'open': return 'green';
    case 'completed': 
    case 'closed': return 'blue';
    case 'under_review': 
    case 'pending': 
    case 'review': return 'yellow';
    case 'archived': return 'purple';
    case 'on_hold': 
    case 'paused': return 'indigo';
    case 'cancelled': 
    case 'rejected': return 'red';
    case 'intake/pre-dispute': 
    case 'intake': 
    case 'pre-dispute': return 'gray';
    case 'evaluation': return 'yellow';
    case 'discovery': return 'blue';
    case 'resolution_process': 
    case 'resolution': return 'green';
    case 'post-resolution': return 'purple';
    default: return 'gray';
  }
};

export const getMatterStatusColor = (
  status: string | undefined | null, 
  matterStatuses: Array<{ label: string; value: string }> = []
): string => {
  if (!status || typeof status !== 'string' || status.trim() === '') {
    return 'gray';
  }

  const normalizedStatus = status.trim().toLowerCase();
  
  const statusConfig = matterStatuses.find(s => 
    s.value.toLowerCase() === normalizedStatus || 
    s.label.toLowerCase() === normalizedStatus
  );
  
  if (statusConfig) {
    const colorMap: Record<string, string> = {
      'intake': 'gray',
      'evaluation': 'yellow',
      'discovery': 'blue',
      'resolution_process': 'green',
      'resolution': 'green',
      'post-resolution': 'purple',
    };
    
    return colorMap[statusConfig.value.toLowerCase()] || 'gray';
  }

  return getStatusColor(status);
};

/**
 * What a matter's status badge says when the record carries no status.
 *
 * "Unknown" was wrong twice over: a live matter with nothing recorded is not in
 * an unknown state, it is simply one nobody has moved along the ladder yet —
 * and "Unknown" reads like an error to the lawyer looking at it. A matter that
 * exists and is being worked is ACTIVE, so that is the display default, in the
 * status vocabulary's own words when it has one (`Active` / `Open` from
 * `/dropdown/get_matter_status`) and in ours when the list has not arrived.
 *
 * This is a RENDER default only - nothing is written back to the matter.
 */
export const defaultMatterStatusLabel = (
  t: (key: string) => string,
  matterStatuses: Array<{ label: string; value: string }> = []
): string => {
  const preferred = matterStatuses.find((s) => {
    const value = String(s?.value ?? '').toLowerCase();
    const label = String(s?.label ?? '').toLowerCase();
    return value === 'active' || label === 'active' || value === 'open' || label === 'open';
  });
  return preferred?.label || preferred?.value || t('Active');
};

export const getStatusDisplay = (
  status: string | undefined | null,
  t: (key: string) => string,
  matterStatuses: Array<{ label: string; value: string }> = []
): string => {
  if (!status || typeof status !== 'string' || status.trim() === '') {
    return defaultMatterStatusLabel(t, matterStatuses);
  }

  const normalizedStatus = status.trim();
  
  const statusConfig = matterStatuses.find(s => 
    s.value.toLowerCase() === normalizedStatus.toLowerCase() || 
    s.label.toLowerCase() === normalizedStatus.toLowerCase()
  );
  
  if (statusConfig) {
    return statusConfig.label || statusConfig.value;
  }

  return t(normalizedStatus) !== normalizedStatus ? t(normalizedStatus) : normalizedStatus;
};

const getInitials = (name?: string | null): string => {
  if (!name || typeof name !== 'string') return 'U';
  const parts = name.trim().split(' ').filter(p => p.length > 0);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const MatterDetailsSkeleton: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-6 w-20" />
    </div>
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);
// Helper function to determine user's access level for a matter
const getUserAccessLevel = (
  userInfo: any,
  collaborators: any[],
  matterData: MatterData | null
): 'edit' | 'admin' | 'view' => {
  if (!collaborators?.length) {
    return 'view';
  }

  const userUUID = String(userInfo?.UserUID || '').toLowerCase().trim();
  const userID = userInfo?.UserID;

  // First, try to find by collaborator UUID
  let userCollaborator = collaborators.find((collab: any) =>
    String(collab.collaborator_id || '').toLowerCase().trim() === userUUID
  );

  // If found in collaborators, return their access level
  if (userCollaborator) {
    return (userCollaborator.access as 'edit' | 'admin' | 'view') || 'view';
  }

  // If not found in collaborators, check if user is the matter creator
  if (matterData && String(matterData.UserID) === String(userID)) {
    return 'admin'; // Matter creators have admin access
  }

  // Default to view if not found
  return 'view';
};

/**
 * Are two data-source option lists the same list?
 *
 * The analyze wizard's options are re-mapped from scratch on every fetch, so a
 * plain `setState(next)` always swaps in a new array and re-renders even when
 * nothing changed. Comparing by content keeps that render - and any effect
 * hanging off it - from firing for an unchanged list.
 */
export const sameOptions = (
  a: { value: string; label: string }[],
  b: { value: string; label: string }[]
): boolean =>
  a.length === b.length &&
  a.every((opt, i) => opt.value === b[i].value && opt.label === b[i].label);

/**
 * Merge an engine-reported step into the analysis list - APPENDING one the
 * opening announcement did not include.
 *
 * The opening list is not guaranteed to be the whole pipeline:
 * `_build_active_steps()` decides the real one at runtime. Mapping over the
 * list therefore dropped, silently, any stage the engine reported that wasn't
 * already in it - a late or extra stage simply never appeared.
 */
export const upsertAnalysisStep = (
  steps: Step[],
  step: string,
  status: Step['status'],
  message?: string
): Step[] => {
  const index = steps.findIndex((s) => s.step === step);
  if (index === -1) {
    return [...steps, { step, status, messages: message ? [message] : [] }];
  }
  return steps.map((s, i) =>
    i === index
      ? { ...s, status, messages: message ? [...s.messages, message] : s.messages }
      : s
  );
};

const MatterDetails: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { matterId } = useParams<RouteParams>();
  const userType = getUserType();
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const { hasAiFeatureAccess, hasSectionAccess, checkSectionGate, hasMatterAiAccess, checkAiFeatureGate } = useLicenseGate();

  /*
    MATTER ECONOMICS IS ITS OWN DOOR.

    Budgets, time entries, matter pricing and outcomes are the €19 Matter
    economics module, gated on `matter_economics` - NOT on `finance`, which
    every Starter seat already carries for invoices and payments. Gating this
    tab on `finance` would have opened it for every firm on the platform.

    The tab is NOT SHOWN when the module is not licensed (only when licensing
    is enforced - `hasSectionAccess` answers yes in pilot). A locked tab still
    mounted the parts around it that read `/matters/<id>/economics`, and each
    403 raised the upgrade modal on simply opening a matter. What a package
    includes is shown on the billing page, like the sidebar. A stale
    `?tab=Economics` falls back to Details (see `visibleTab`).
  */
  const hasMatterEconomics = hasSectionAccess('matter_economics');
  /* The Assistant pane is a research session in the matter: no `research`,
     no pane (and no `/research/chat-groups` reads behind it). */
  const hasResearch = hasSectionAccess('research');
  /* Matter AI - analyse / re-analyse. Starter keeps its matters, without AI. */
  const hasMatterAi = hasMatterAiAccess();

  const [dataState, setDataState] = useState<DataState>({
    data: null,
    lastFetched: null,
    isStale: false,
    error: null,
    hasTriedLoading: false,
  });
  
  const [loadingInitialData, setLoadingInitialData] = useState<boolean>(true);
  const [isEditingMatterInfo, setIsEditingMatterInfo] = useState(false);
  const [editedMatterFields, setEditedMatterFields] = useState<any>({});
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const matterStatuses = useSelector((state: RootState) => state.dropdown.dropdowns.matterStatuses || []);

  /**
   * The workspace: pane layout, the selected row, the reader's content and
   * the active tab, from the ONE shared hook.
   *
   * Keyed by matter id AND by user, so each matter remembers its own
   * arrangement and nobody inherits a colleague's - see `surface/layout` and
   * `services/layout/layoutStore`.
   */
  const workspace = useMatterWorkspace(matterId, {
    // The raw query param: the hook checks it against the surface's declared
    // tabs (see `surface/layout`) and falls back to the first, so a stale
    // `?tab=` from a renamed tab cannot leave the canvas blank.
    initialTab: new URLSearchParams(location.search).get('tab') || undefined,
    onTabChange: (tab) => {
      /* The gate answers with the modal and returns false, so a locked tab
         never becomes the active one and the URL never claims it did. */
      if (tab === 'Economics' && !checkSectionGate('matter_economics')) return;
      history.push(`/matters/${matterId}?tab=${tab}`);
    },
  });
  const {
    panels,
    api: surfaceApi,
    activeTab: workspaceTab,
    setActiveTab,
    goToTab: handleTabClick,
    selection,
    readerContent,
  } = workspace;

  /* A locked Economics tab (from a stale `?tab=` or a saved layout) renders
     Details instead, so the economics workspace never mounts and fetches. */
  const activeTab = workspaceTab === 'Economics' && !hasMatterEconomics ? 'Details' : workspaceTab;

  const matterData = dataState.data;
  const prefetchedData = useSelector((state: RootState) => state.matters.prefetchedMatterData);


  const matterName = matterData?.MatterName || t('Untitled matter');

  // Clear any stale tour lock from previous pages (e.g. Matters list)
  useEffect(() => {
    clearActiveTourLock();
  }, []);

  // ── Orientation Tour ─────────────────────────────────────────
  const {
    isRunning: isOrientationTourRunning,
    handleJoyrideCallback: handleOrientationTourCallback,
  } = useFeatureTour({
    tourId: 'matter-detail-orientation',
    steps: matterDetailOrientationSteps,
    readyToShow: !loadingInitialData && !!matterData,
    autoStartDelay: 600,
  });

  const handleOrientationBeforeStep = useCallback(
    (step: any) => {
      const tab = step?.data?.tab;
      if (tab && tab !== activeTab) {
        // The tour drives the tab directly: it must NOT push a URL or clear
        // the selection the way a click on the bar does.
        setActiveTab(tab);
      }
    },
    [activeTab, setActiveTab],
  );

  const [sources, setSources] = useState<any>(null);
  const [questionnaire, setQuestionnaire] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [suggestedTasks, setSuggestedTasks] = useState<any[]>([]);
  const [matterTasks, setMatterTasks] = useState<any[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [suggestedTasksLoading, setSuggestedTasksLoading] = useState(false);
  const [matterTasksLoading, setMatterTasksLoading] = useState(false);
  const [userAccessLevel, setUserAccessLevel] = useState<'edit' | 'admin' | 'view'>('view');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<{
    last_analyzed_at: string | null;
    processing_status: string | null;
    documents_since_analysis: number;
    has_been_analyzed: boolean;
    context_changed_since_analysis?: boolean;
    correlation_id?: string | null;
  } | null>(null);

  // Data source selection for analysis
  const [availableDataSources, setAvailableDataSources] = useState<{ value: string; label: string }[]>([]);

  // Analysis progress tracking (PubSub-driven)
  const [analysisProgress, setAnalysisProgress] = useState<Step[]>([]);
  const [analysisCorrelationId, setAnalysisCorrelationId] = useState<string | null>(null);
  const [analysisPercentage, setAnalysisPercentage] = useState(0);
  const [isAnalysisRunning, setIsAnalysisRunning] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const analysisHandlerRef = useRef<((...args: any[]) => void) | null>(null);
  const analysisGroupRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const dataStateRef = useRef<any>(null);
  // Set to true once loadAll (or the prefetch path) has finished - used to guard
  // standalone effects from re-firing immediately after initial load completes.
  const initialLoadDoneRef = useRef(false);

  const people = useSelector((state: RootState) => state.orgPeople?.people || []);

  /**
   * The prefetched matter is a FIRST-PAINT shortcut and is consumed once.
   *
   * `refreshSummary` used to prefer it on every call, so for as long as the
   * prefetch sat in the store any refresh - including the one right after a
   * save - re-rendered from the PRE-SAVE snapshot. The edit looked like it had
   * not persisted until a hard reload.
   */
  const prefetchConsumed = useRef(false);

  useEffect(() => { prefetchConsumed.current = false; }, [matterId]);

  const refreshSummary = useCallback(async (showLoading = false) => {
    if (showLoading) setLoadingInitialData(true);

    try {
      let detailsResult = prefetchConsumed.current ? null : prefetchedData;
      prefetchConsumed.current = true;

      if (!detailsResult) {
        detailsResult = await dispatch(fetchMatterDetails(matterId));
      }

      if (detailsResult) {
        const matterData = {
          ...detailsResult,
          uuid: matterId,
          MatterName: detailsResult.MatterName || detailsResult.title || '',
          Description: detailsResult.Description || detailsResult.description || '',
          MatterType: detailsResult.MatterType || detailsResult.type || '',
          Jurisdiction: detailsResult.Jurisdiction || detailsResult.jurisdiction || '',
          MatterStage: detailsResult.status_name || detailsResult.stage_name || '',
          created_at: detailsResult.created_at || new Date().toISOString(),
          updated_at: detailsResult.updated_at || detailsResult.UpdatedAt || new Date().toISOString(),
          due_date: detailsResult.due_date || detailsResult.DueDate || null,
          language: detailsResult.language || detailsResult.Language || '',
          collaborators: detailsResult.collaborators || detailsResult.Collaborators || [],
        };
        setDataState({
          data: matterData,
          lastFetched: Date.now(),
          isStale: false,
          error: null,
          hasTriedLoading: true,
        });
        dataStateRef.current = matterData;
        setEditedMatterFields(matterData || {});
        // Transform collaborators from API format to UI format
        const transformedCollaborators = (matterData.collaborators || []).map((collab: any) => {
          return {
            ...collab,
            name: collab.FullName || collab.name || '',
            initials: getInitials(collab.FullName || collab.name),
            email: collab.Email || collab.email,
            avatarURL: collab.ProfilePicture || collab.avatarURL,
          };
        });
        setCollaborators(transformedCollaborators);
        return matterData;
      } else {
        // If detailsResult is null, it means the matter doesn't exist
        // Mark as having tried loading so we don't keep retrying
        setDataState(prev => ({
          ...prev,
          error: 'Matter does not exist or has been deleted',
          hasTriedLoading: true,
        }));
      }
    } catch (err) {
      const errorMessage = (err as any)?.message || t('Failed to load matter data');
      setDataState(prev => ({
        ...prev,
        error: errorMessage,
        hasTriedLoading: true,
      }));
      console.error('Failed to load initial data:', err);
    } finally {
      // Keep loading state if the matter is currently being processed —
      // the processing-detection effect will take over and show the
      // ProgressTracker instead of flashing empty content.
      if (showLoading) {
        const status = dataStateRef.current?.processing_status;
        const isProcessing = status === 'running' || status === 'queued' || status === 'processing';
        if (!isProcessing) {
          setLoadingInitialData(false);
        }
      }
    }
    return null;
  }, [matterId, dispatch, t, prefetchedData]);

  const refreshSources = useCallback(async () => {
    setSourcesLoading(true);
    try {
      const result = await dispatch(fetchMatterSources(matterId));
      if (result) {
        setSources(result);
      }
    } catch (error) {
      console.error('Failed to load sources:', error);
    } finally {
      setSourcesLoading(false);
    }
  }, [matterId, dispatch]);

  const refreshQuestionnaire = useCallback(async () => {
    setQuestionnaireLoading(true);
    try {
      const result = await dispatch(fetchQuestionnaire(matterId));
      if (result) {
        setQuestionnaire(result);
      }
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
    } finally {
      setQuestionnaireLoading(false);
    }
  }, [matterId, dispatch]);

  const refreshNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const result = await dispatch(fetchMatterNotes(matterId));
      if (result) {
        setNotes(result);
      }
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      setNotesLoading(false);
    }
  }, [matterId, dispatch]);

  /**
   * Suggestions removed in this session - accepted or dismissed.
   *
   * The removal IS persisted, so a fresh fetch will not return them. This
   * guards the window in between: a `refreshSuggestedTasks` already in flight
   * when the accept lands would otherwise resolve with the old list and put
   * the accepted suggestion straight back. Matched on title as well as id,
   * because a suggestion with no server id falls back to a title+index key
   * and the indices shift the moment one is removed.
   */
  const removedSuggestions = useRef<{ ids: Set<string>; titles: Set<string> }>({
    ids: new Set(),
    titles: new Set(),
  });

  /** Forget the tombstones when the matter changes - they were about that matter. */
  useEffect(() => {
    removedSuggestions.current = { ids: new Set(), titles: new Set() };
  }, [matterId]);

  const keepSuggestion = useCallback((task: any, index: number): boolean => {
    const { ids, titles } = removedSuggestions.current;
    const title = String(task?.TaskTitle ?? task?.title ?? '').trim();
    return !ids.has(suggestionId(task, index)) && !(title && titles.has(title));
  }, []);

  /*
    Connected work - research, drafts, insights, reviews, tables - in one call.
    Owned here rather than in the pane because the rail badge shows the count
    while the pane is closed, and an unmounted pane cannot fetch.
  */
  const connected = useConnectedWork(matterId);

  const refreshSuggestedTasks = useCallback(async () => {
    setSuggestedTasksLoading(true);
    try {
      const result = await dispatch(fetchSuggestedTasks(matterId));
      if (Array.isArray(result)) {
        setSuggestedTasks(result.filter(keepSuggestion));
      }
    } catch (error) {
      console.error('Failed to load suggested tasks:', error);
    } finally {
      setSuggestedTasksLoading(false);
    }
  }, [matterId, dispatch, keepSuggestion]);

  const refreshMatterTasks = useCallback(async () => {
    setMatterTasksLoading(true);
    try {
      const result = await dispatch(fetchAllTasks({ matter_uuid: matterId, limit: 100 }));
      if (result?.tasks) {
        setMatterTasks(result.tasks);
      }
    } catch (error) {
      console.error('Failed to load matter tasks:', error);
    } finally {
      setMatterTasksLoading(false);
    }
  }, [matterId, dispatch]);


  useEffect(() => {
    if (!matterId) return;

    if (prefetchedData) {
      // Prefetch path: summary already partially available - load the rest in parallel,
      // then drop the skeleton once all critical data (sources, analysis status, doc types) is ready.
      refreshSummary(false).then(async () => {
        await Promise.all([
          refreshSources(),
          /*
            THE COUNTS MUST BE RIGHT ON LOAD.

            These three were lazy - each tab fetched its own list the first
            time it was opened - so the Context pane's "At a glance" read 0
            for anything you had not clicked into yet. There is no counts or
            summary object on `/matters/details` to read instead, so the same
            list calls the tabs make are simply hoisted to matter load. They
            are small, and the per-tab guards below already skip refetching
            once the data is here, so this moves the work rather than adding
            it - the tabs now open instantly too.
          */
          refreshQuestionnaire(),
          refreshNotes(),
          refreshMatterTasks(),
          refreshSuggestedTasks(),
          dispatch(getAnalysisStatus(matterId)).then((status: any) => {
            if (status) setAnalysisStatus(status);
          }).catch(() => {}),
        ]);
        initialLoadDoneRef.current = true;
        const status = dataStateRef.current?.processing_status;
        const isProcessing = status === 'running' || status === 'queued' || status === 'processing';
        if (!isProcessing) {
          setLoadingInitialData(false);
        }
      });
      setTimeout(() => {
        dispatch(clearPrefetchedMatterData());
      }, 100);
    } else if (!dataState.data && !dataState.hasTriedLoading) {
      // Fresh load: wait for ALL data the user sees immediately on the Details tab before
      // dropping the skeleton. Secondary data (org people, data sources) loads in the
      // background after the skeleton drops - see effects below.
      const loadAll = async () => {
        setLoadingInitialData(true);
        await Promise.all([
          refreshSummary(false),
          refreshSources(),
          /*
            THE COUNTS MUST BE RIGHT ON LOAD.

            These three were lazy - each tab fetched its own list the first
            time it was opened - so the Context pane's "At a glance" read 0
            for anything you had not clicked into yet. There is no counts or
            summary object on `/matters/details` to read instead, so the same
            list calls the tabs make are simply hoisted to matter load. They
            are small, and the per-tab guards below already skip refetching
            once the data is here, so this moves the work rather than adding
            it - the tabs now open instantly too.
          */
          refreshQuestionnaire(),
          refreshNotes(),
          refreshMatterTasks(),
          refreshSuggestedTasks(),
          dispatch(getAnalysisStatus(matterId)).then((status: any) => {
            if (status) setAnalysisStatus(status);
          }).catch(() => {}),
        ]);
        initialLoadDoneRef.current = true;
        // Check if matter is being processed - if so, keep skeleton
        // (the processing-detection effect will take over)
        const status = dataStateRef.current?.processing_status;
        const isProcessing = status === 'running' || status === 'queued' || status === 'processing';
        if (!isProcessing) {
          setLoadingInitialData(false);
        }
      };
      loadAll();
    }
  }, [
    matterId, prefetchedData, dispatch, refreshSummary, refreshSources,
    refreshQuestionnaire, refreshNotes, refreshMatterTasks, refreshSuggestedTasks,
    dataState.data, dataState.hasTriedLoading,
  ]);

  /*
    The org's "archived" status rung used to be looked up here, because
    archiving WAS a status change. It is a real soft delete now, so nothing
    depends on the ladder having that rung and the lookup is gone with it.
  */

  // Fetch org people for collaborator selection - deferred until after the skeleton drops
  // so it doesn't block the critical-path Promise.all in loadAll.
  useEffect(() => {
    if (loadingInitialData) return;
    if (people.length === 0) {
      dispatch(fetchOrgPeople() as any);
    }
  }, [dispatch, people.length, loadingInitialData]);

  // Calculate user access level based on collaborators
  useEffect(() => {
    const accessLevel = getUserAccessLevel(userInfo, collaborators, matterData);
    setUserAccessLevel(accessLevel);
  }, [matterData, userInfo, collaborators]);

  useEffect(() => {
    if (activeTab === 'Documents' && !sources) {
      refreshSources();
    }
    if (activeTab === 'Q&As' && questionnaire.length === 0) {
      refreshQuestionnaire();
    }
    if (activeTab === 'Notes' && notes.length === 0) {
      refreshNotes();
    }
    /*
      TWO LISTS, TWO GUARDS.

      This was one `&&` across both: suggestions were only fetched when the
      matter ALSO had no tasks. That held while both were lazy, but hoisting
      `refreshMatterTasks` into the initial load made `matterTasks.length`
      non-zero for any matter with tasks - so the compound condition never
      passed again and the Suggestions sub-tab silently stopped loading.
    */
    if (activeTab === 'Tasks' && suggestedTasks.length === 0) {
      refreshSuggestedTasks();
    }
    if (activeTab === 'Tasks' && matterTasks.length === 0) {
      refreshMatterTasks();
    }
  }, [activeTab, sources, questionnaire.length, notes.length, suggestedTasks.length, matterTasks.length, refreshSources, refreshQuestionnaire, refreshNotes, refreshSuggestedTasks, refreshMatterTasks]);

  /**
   * @param patch fields a child just saved.
   *
   * Applied to the displayed matter BEFORE the refetch, so every changed
   * value re-renders together and immediately - the Details reading view, the
   * key facts, the timeline and the Context pane all read the same object.
   * Without it the page waited on the round trip, and any field the details
   * blob had not caught up with silently reverted to its old value.
   */
  const handleChildUpdate = useCallback((patch?: Record<string, any>) => {
    if (patch && Object.keys(patch).length > 0) {
      setDataState((prev) => {
        if (!prev.data) return prev;
        const merged = { ...prev.data, ...patch };
        dataStateRef.current = merged;
        return { ...prev, data: merged };
      });
      setEditedMatterFields((prev: any) => ({ ...prev, ...patch }));
    }
    refreshSummary(false);
    if (activeTab === 'Documents') {
      refreshSources();
    } else if (activeTab === 'Q&As') {
      refreshQuestionnaire();
    } else if (activeTab === 'Notes') {
      refreshNotes();
    } else if (activeTab === 'Tasks') {
      refreshSuggestedTasks();
      refreshMatterTasks();
    }
  }, [activeTab, refreshSummary, refreshSources, refreshQuestionnaire, refreshNotes, refreshSuggestedTasks, refreshMatterTasks]);

  // Re-fetch analysis status whenever processing_status changes after initial load.
  // Uses initialLoadDoneRef (not loadingInitialData state) as the guard so that
  // the effect does NOT re-trigger the moment the skeleton drops - only genuine
  // processing_status changes on the matter will cause a re-fetch.
  useEffect(() => {
    if (!initialLoadDoneRef.current) return; // loadAll has not completed yet
    if (matterId) {
      dispatch(getAnalysisStatus(matterId)).then((status: any) => {
        if (status) setAnalysisStatus(status);
      });
    }
  }, [matterId, dispatch, matterData?.processing_status]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
    Fetch available data sources for the analyze wizard.

    ONE FETCH PER MOUNT - this effect used to loop `/datasources` without
    bound. It depended on `hasAiFeatureAccess` (a function identity, which was
    new on every render while the license payload was absent) AND it ended in
    `setAvailableDataSources(readySources)` with a freshly mapped array, so
    each fetch re-rendered, the re-render re-ran the effect, and the effect
    fetched again. The gate is memoized at the source now (useLicenseGate);
    here the dep is the BOOLEAN it returns, and the state is only replaced when
    the list actually differs - either one alone breaks the cycle.
  */
  const canUseDataSourceIndexing = hasAiFeatureAccess('data_source_indexing');

  useEffect(() => {
    if (!canUseDataSourceIndexing) return;
    let cancelled = false;
    const loadDataSources = async () => {
      const dsResult: any = await dispatch(fetchDataSources({ page: 1, limit: 100 }));
      if (cancelled || !dsResult?.dataSources) return;
      const readySources = dsResult.dataSources
        .filter((ds: any) => {
          const idxStatus = (ds.indexer_status || '').toLowerCase();
          const dsStatus = (ds.status || '').toLowerCase();
          return (
            ['success', 'completed'].includes(idxStatus) ||
            ['ready', 'active'].includes(dsStatus)
          );
        })
        .map((ds: any) => ({
          value: ds.uuid,
          label: ds.name || t('Unnamed data source'),
        }));
      setAvailableDataSources((prev) => (sameOptions(prev, readySources) ? prev : readySources));
    };
    loadDataSources();
    return () => { cancelled = true; };
  }, [dispatch, canUseDataSourceIndexing]); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount: detect if matter is currently processing and reconnect to PubSub
  useEffect(() => {
    if (!matterData || isAnalysisRunning) return;

    const processingStatus = matterData.processing_status;
    const isCurrentlyProcessing = processingStatus === 'running' || processingStatus === 'queued' || processingStatus === 'processing';

    if (isCurrentlyProcessing) {
      const corrId = matterData.processing_correlation_id || analysisStatus?.correlation_id;
      if (corrId) {
        connectToAnalysisPubSub(corrId);
      } else {
        // No correlation ID available, just show a simple running state
        setIsAnalysisRunning(true);
        setIsAnalyzing(true);
      }
      // Now that analysis tracking is active, release the loading skeleton
      initialLoadDoneRef.current = true;
      setLoadingInitialData(false);
    }
  }, [matterData?.processing_status, matterData?.processing_correlation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup PubSub on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const multiHubService = getMultiHubSocketService();
      if (analysisGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: analysisGroupRef.current });
      }
      if (analysisHandlerRef.current) {
        multiHubService.off('ai_processing', '*', analysisHandlerRef.current);
      }
    };
  }, []);

  // --- Analysis PubSub event handler ---
  const handleAnalysisProgress = useCallback((raw: any) => {
    if (!mountedRef.current) return;

    const event = Array.isArray(raw) ? raw[0] : raw || {};
    const innerData = event.data || {};
    const data = { ...event, ...innerData };

    const rawStatus = data.status ?? '';
    const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : String(rawStatus).toLowerCase();
    const subType = data.sub_type || data.event_name || data.eventType || data.event_type || null;
    const stepName = data.step_name || data.stepName || data.step || null;
    const message = data.message || '';
    const progressPercent = data.progress_percent || 0;

    const isSystemInit = subType === 'matter_initiated' || subType === 'matter.initiated' || (status === 'started' && Array.isArray(data.steps));
    const isCompletion = status === 'completed' || subType === 'complete' || data.event_name === 'ai_processing_complete';
    const isFailure = status === 'failed' || data.error;
    const isWelcome = stepName === 'welcome' || subType === 'welcome' || message?.toLowerCase().includes('preparing');
    const isTechnical = (stepName === 'system' || subType === 'system' || subType === 'connected' || subType === 'disconnected') && !message;

    if (isTechnical || isWelcome) return;

    if (isSystemInit) {
      const normalizedSteps: Step[] = (data.steps || []).map((s: any) => ({
        step: s.step || s.step_name || 'Analysis',
        status: 'Pending' as const,
        messages: [],
      }));
      setAnalysisProgress(normalizedSteps);
      if (progressPercent) setAnalysisPercentage(progressPercent);

      const idToUse = data.correlationId || data.correlation_id || analysisCorrelationId;
      if (idToUse && data.startTime) {
        localStorage.setItem(`matter_creation_start:${idToUse}`, data.startTime);
      }
      return;
    }

    if (subType === 'step_started' && stepName) {
      setAnalysisProgress(prev =>
        upsertAnalysisStep(prev, stepName, 'In Progress', message || undefined)
      );
      if (progressPercent) setAnalysisPercentage(progressPercent);
      return;
    }

    if (subType === 'step_progress' && message) {
      setAnalysisProgress(prev => prev.map(s =>
        s.status === 'In Progress' ? { ...s, messages: [...s.messages, message] } : s
      ));
      if (progressPercent) setAnalysisPercentage(progressPercent);
      return;
    }

    if (subType === 'step_completed' && stepName) {
      setAnalysisProgress(prev =>
        upsertAnalysisStep(prev, stepName, 'Completed', message || undefined)
      );
      if (progressPercent) setAnalysisPercentage(progressPercent);
      return;
    }

    if (isCompletion) {
      // Mark any remaining steps as completed
      setAnalysisProgress(prev => prev.map(s =>
        s.status !== 'Completed' ? { ...s, status: 'Completed' as const } : s
      ));
      setAnalysisPercentage(100);
      setAnalysisComplete(true);

      // Clean up PubSub
      const multiHubService = getMultiHubSocketService();
      if (analysisGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: analysisGroupRef.current });
      }
      if (analysisHandlerRef.current) {
        multiHubService.off('ai_processing', '*', analysisHandlerRef.current);
      }
      analysisGroupRef.current = null;
      analysisHandlerRef.current = null;

      // After a short delay, refresh data and only then hide the progress overlay
      setTimeout(async () => {
        if (!mountedRef.current) return;

        // Show the loading skeleton while we fetch fresh data
        setLoadingInitialData(true);

        // Hide progress tracker state (skeleton takes over)
        setIsAnalysisRunning(false);
        setIsAnalyzing(false);
        setAnalysisComplete(false);
        setAnalysisProgress([]);
        setAnalysisPercentage(0);
        setAnalysisCorrelationId(null);

        // Reset stale data so lazy-loading guards allow re-fetch
        setSources(null);
        setQuestionnaire([]);
        setNotes([]);
        setSuggestedTasks([]);
        setMatterTasks([]);

        // Refresh ALL data tabs - analysis may have produced new data everywhere
        // Await the summary so the skeleton stays visible until core data arrives
        await refreshSummary(true);
        refreshSources();
        refreshQuestionnaire();
        refreshNotes();
        refreshSuggestedTasks();
        refreshMatterTasks();
        dispatch(getAnalysisStatus(matterId)).then((s: any) => {
          if (s) setAnalysisStatus(s);
        });
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Analysis completed successfully'),
          modalType: 'success',
          duration: 3000,
        }));
      }, 2000);
      return;
    }

    if (isFailure) {
      const errorMsg = toErrorText(data.error) || toErrorText(message) || t('Analysis failed');
      setAnalysisProgress(prev => prev.map(s =>
        s.status === 'In Progress' ? { ...s, status: 'Failed' as const, messages: [...s.messages, errorMsg] } : s
      ));

      // Clean up PubSub
      const multiHubService = getMultiHubSocketService();
      if (analysisGroupRef.current) {
        multiHubService.emit('ai_processing', 'leave', { group: analysisGroupRef.current });
      }
      if (analysisHandlerRef.current) {
        multiHubService.off('ai_processing', '*', analysisHandlerRef.current);
      }
      analysisGroupRef.current = null;
      analysisHandlerRef.current = null;

      setTimeout(() => {
        if (!mountedRef.current) return;
        setIsAnalysisRunning(false);
        setIsAnalyzing(false);
        setAnalysisProgress([]);
        setAnalysisPercentage(0);
        setAnalysisCorrelationId(null);
        refreshSummary(false);
        dispatch(showFeedbackModal({
          showModal: true,
          message: `Analysis failed: ${errorMsg}`,
          modalType: 'error',
          duration: 5000,
        }));
      }, 3000);
      return;
    }
  }, [analysisCorrelationId, dispatch, matterId, refreshSummary, refreshSources, refreshQuestionnaire, refreshNotes, refreshSuggestedTasks, refreshMatterTasks]);

  // Keep handler ref in sync
  useEffect(() => {
    analysisHandlerRef.current = handleAnalysisProgress;
  }, [handleAnalysisProgress]);

  // --- Connect to PubSub for analysis tracking ---
  const connectToAnalysisPubSub = useCallback(async (correlationId: string) => {
    const multiHubService = getMultiHubSocketService();
    const group = `matter_${correlationId}`;

    try {
      await multiHubService.connectToHub('ai_processing');
      multiHubService.emit('ai_processing', 'join', { group });
      multiHubService.on('ai_processing', '*', handleAnalysisProgress);
      analysisGroupRef.current = group;
      analysisHandlerRef.current = handleAnalysisProgress;

      setAnalysisCorrelationId(correlationId);
      setIsAnalysisRunning(true);
      setIsAnalyzing(true);

      // Store start time for elapsed timer
      localStorage.setItem(`matter_creation_start:${correlationId}`, Date.now().toString());
    } catch (error) {
      console.error('Failed to connect to analysis PubSub:', error);
    }
  }, [handleAnalysisProgress]);

  // A matter just created from the form navigates straight here with its
  // correlation id in nav state - BEFORE the details fetch has flipped
  // processing_status to 'running'. Connect to the hub immediately so the
  // processing view is on screen from the first paint. (A cold click-in later
  // uses the persisted processing_correlation_id via the on-mount effect above.)
  const seededFromNavRef = useRef(false);
  useEffect(() => {
    if (seededFromNavRef.current) return;
    const navCorr = (location.state as any)?.processingCorrelationId;
    if (navCorr && !analysisGroupRef.current) {
      seededFromNavRef.current = true;
      connectToAnalysisPubSub(navCorr);
      initialLoadDoneRef.current = true;
      setLoadingInitialData(false);
      // Clear the nav state so a refresh/back doesn't re-seed a finished run.
      history.replace({ pathname: location.pathname });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback poll: PubSub is the fast path for completion, but if it never
  // connects the view would otherwise spin forever. While a run is live, poll
  // the analysis status and resolve out of the running state when it finishes,
  // then refetch so the matter re-renders with its results.
  useEffect(() => {
    if (!isAnalysisRunning || !matterId) return;
    // One poll at a time: the status call lists the matter's blobs and can take
    // longer than the interval, and stacked requests starve the rest of the page.
    let inFlight = false;
    const id = setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      dispatch(getAnalysisStatus(matterId)).finally(() => { inFlight = false; }).then((s: any) => {
        const st = String(s?.processing_status || '').toLowerCase();
        if (st === 'completed' || st === 'completed_with_errors' || st === 'failed') {
          setIsAnalysisRunning(false);
          setIsAnalyzing(false);
          if (s) setAnalysisStatus(s);
          refreshSummary(true);
        }
      }).catch(() => { /* transient - keep polling */ });
    }, 5000);
    return () => clearInterval(id);
  }, [isAnalysisRunning, matterId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnalyzeMatter = useCallback(async (config: AnalyzeWizardResult) => {
    if (!matterId || isAnalyzing) return;
    dispatch(hideModal());
    setIsAnalyzing(true);

    try {
      const result = await dispatch(analyzeMatter(matterId, config.analysisOptions, config.selectedDataSourceUuids, config.selectedIndexes));
      if (result) {
        const correlationId = result.correlationId || result.correlation_id;
        if (correlationId) {
          await connectToAnalysisPubSub(correlationId);
        } else {
          // Fallback: no correlation ID, just poll status
          refreshSummary(false);
          setIsAnalyzing(false);
        }
      } else {
        setIsAnalyzing(false);
      }
    } catch (error) {
      console.error('Failed to analyze matter:', error);
      setIsAnalyzing(false);
    }
  }, [matterId, isAnalyzing, dispatch, connectToAnalysisPubSub, refreshSummary]);

  const openAnalyzeWizard = useCallback(() => {
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Analyze matter'),
      icon: <PiLightningLight className="w-6 h-6 text-gray-700" />,
      width: '640px',
      content: (
        <AnalyzeWizardModal
          jurisdiction={matterData?.jurisdiction || matterData?.Jurisdiction || ''}
          hasBeenAnalyzed={analysisStatus?.has_been_analyzed || false}
          availableDataSources={availableDataSources}
          onLaunch={handleAnalyzeMatter}
          onClose={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => dispatch(hideModal()),
    }));
  }, [dispatch, t, matterData, analysisStatus, availableDataSources, handleAnalyzeMatter]);

  /**
   * What the tabs can ask of the surface around them.
   *
   * Deliberately small, and a CONTEXT rather than four more props on
   * `MatterDetailsContent` - that component already forwards 22 and none of
   * this concerns the tabs it does not reach. See `MatterSurfaceContext`.
   */
  /**
   * Suggestions paired with a stable client id.
   *
   * They have no server id until they are accepted, so the id is derived once
   * here and reused as the row key AND the selection id - the two must agree
   * or selecting a suggestion would open a different one.
   */
  const suggestionEntries = useMemo(
    () => suggestedTasks.map((task, index) => ({ id: suggestionId(task, index), task })),
    [suggestedTasks]
  );

  /**
   * Remove a suggestion - accepted or dismissed, same path.
   *
   * THE ACCEPTED-SUGGESTION-COMES-BACK BUG. This used to update local state
   * only. The accept then called `onUpdate`, which re-fetches suggestions,
   * and the backend still held the accepted one - so it reappeared and the
   * same task showed in both Suggestions and Active.
   *
   * The remaining list is written back with `updateSuggestedTasks` BEFORE the
   * caller refreshes, so the refetch reads a list that no longer contains it.
   * Awaited, so callers can sequence on it.
   */
  const removeSuggestion = useCallback(async (id: string) => {
    const removed = suggestedTasks.find((task, index) => suggestionId(task, index) === id);
    // Already gone - a double-click on Accept, or a stale container. Doing
    // nothing also stops a second persist racing the first.
    if (!removed) return;

    const remaining = suggestedTasks.filter((task, index) => suggestionId(task, index) !== id);
    const title = String((removed as any)?.TaskTitle ?? (removed as any)?.title ?? '').trim();
    removedSuggestions.current.ids.add(id);
    if (title) removedSuggestions.current.titles.add(title);

    // Optimistic: the row disappears immediately, before the round trip.
    setSuggestedTasks(remaining);
    await dispatch(updateSuggestedTasks(matterId, remaining));
  }, [suggestedTasks, matterId, dispatch]);

  const detailTitle = useMemo(() => {
    if (!selection) return t('Details');
    if (selection.mode === 'create') {
      return selection.kind === 'note' ? t('New note')
        : selection.kind === 'qa' ? t('New question')
        : t('New task');
    }
    return selection.kind === 'note' ? t('Note')
      : selection.kind === 'qa' ? t('Question')
      : selection.kind === 'suggestion' ? t('Suggested task')
      : t('Task');
  }, [selection, t]);

  /* The header badge and the Context pane's OVERVIEW chip read the same two
     callbacks, so the "no status recorded" default is decided once. See
     `defaultMatterStatusLabel` for why it is Active and not "Unknown". */
  const getCurrentStatusColor = useCallback(() => {
    const status = matterData?.MatterStage || defaultMatterStatusLabel(t, matterStatuses);
    return getMatterStatusColor(status, matterStatuses) as BadgeColor;
  }, [matterData?.MatterStage, matterStatuses, t]);

  const getCurrentStatusDisplay = useCallback(
    () => getStatusDisplay(matterData?.MatterStage, t, matterStatuses),
    [matterData?.MatterStage, matterStatuses, t]
  );

  /*
    NO HARD DELETE FROM THE MATTER PAGE.

    `handleDeleteMatter` used to sit here and put a "Delete" item in the ⋯
    menu, one click and a dialog away from destroying a live matter. The
    primary destructive action is now Archive (below), and permanent deletion
    lives only in the Archive view on the matters list - where the matter is
    already out of the way and the dialog asks the user to type DELETE. The
    backend enforces the same order.
  */

  const handleSaveMatterInfo = useCallback(async () => {
    try {
      // Only send fields the user actually changed (diff against current matterData)
      const changedFields: Record<string, any> = {};

      if (editedMatterFields.MatterName !== matterData?.MatterName) {
        changedFields.MatterName = editedMatterFields.MatterName;
      }
      if (editedMatterFields.MatterType !== matterData?.MatterType) {
        changedFields.MatterType = editedMatterFields.MatterType;
      }
      if (editedMatterFields.Description !== matterData?.Description) {
        changedFields.Description = editedMatterFields.Description;
      }
      if ((editedMatterFields as any).due_date !== (matterData as any)?.due_date) {
        changedFields.due_date = (editedMatterFields as any).due_date;
      }

      // If nothing changed, just exit - no API call needed
      if (Object.keys(changedFields).length === 0) {
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: t('No changes to save'),
          showModal: true,
          duration: 2000
        }));
        return;
      }

      const editResult = await dispatch(editMatter(matterId, changedFields));
      if (!editResult) return; // Action failed - handleApiError already showed the error toast

      const result = await refreshSummary(false);
      if (result) {
        setEditedMatterFields(result);
      }

      dispatch(showFeedbackModal({
        modalType: 'success',
        message: t('Matter updated successfully'),
        showModal: true,
        duration: 3000
      }));

    } catch (error: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Failed to update matter'),
        showModal: true,
        duration: 3000
      }));
      console.error('Failed to update matter:', error);
      throw error;
    }
  }, [dispatch, matterId, editedMatterFields, matterData, t, refreshSummary]);

  const matterBreadcrumbs = useMemo(() => 
    buildBreadcrumbs(t, matterName, matterId, activeTab),
    [t, matterName, matterId, activeTab]
  );

  /**
   * "Add document" from the overflow menu opens the uploader in a side panel
   * rather than just switching to the Documents tab and leaving the user to
   * find the upload control themselves (UI feedback M-19). On success it
   * refreshes the sources and brings the Documents tab forward so the new file
   * is visible.
   */
  const handleAddDocument = useCallback(() => {
    dispatch(showSidePanel({
      type: 'uploadAttachment',
      showSidePanel: true,
      size: 'md',
      message: t('Upload files'),
      icon: <PiFileLight className="w-5 h-5" />,
      subMsg: t('Evidence, contracts, briefs, pleadings, memos, or other case-related documents.'),
      onConfirm: async (data: any) => {
        const uploadFiles: File[] = data?.files || data;
        if (!uploadFiles?.length || !matterId) return;
        try {
          await dispatch(addFileToMatter(matterId, uploadFiles));
          await refreshSources();
          handleTabClick('Documents');
        } catch (error) {
          dispatch(showFeedbackModal({
            showModal: true,
            modalType: 'error',
            message: t('Upload failed. Please try again.'),
            duration: 3000,
          }));
        }
      },
      files: [],
      onCancel: () => dispatch(hideSidePanel()),
    } as any));
  }, [dispatch, t, matterId, refreshSources, handleTabClick]);

  /** Manage-access side panel - shared by the overflow menu, the collaborator
   *  stack in the header and the activity pane. */
  const openManageAccess = useCallback(() => {
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage access'),
      resourceUuid: matterId,
      resourceType: 'matter',
      collaborators: collaborators.map((c: any) => ({
        ...c,
        user_id: c.collaborator_id || c.user_id || c.UID,
        collaborator_uuid: c.collaborator_id || c.collaborator_uuid || c.UID,
        access: c.access || c.role || c.Role || 'view',
      })),
      currentUserUuid: userInfo?.UserUID,
      /* Was hardcoded `true`. A viewer can now reach this panel through the
         pane's "View access", so the write controls follow their level. */
      canAdmin: userAccessLevel === 'edit' || userAccessLevel === 'admin',
      /*
        The panel hands over the row it already resolved, so the collaborator
        goes up with a real name/email/initials and - the part that was
        actually broken - its access level ON the collaborator object.
        `/matters/add_collaborators` reads `access` per entry and ignores any
        top-level permission, so "edit" used to be dropped and every add
        landed as a nameless 'view'.
      */
      addCollaborator: async (uuid: string, userUuid: string, access: string, collab?: any) => {
        await dispatch(addMatterCollaborators({
          MatterID: uuid,
          Collaborators: [{
            id: userUuid,
            access: access as any,
            name: collab?.name || '',
            email: collab?.email || '',
            initials: collab?.initials || '',
            avatarUrl: collab?.profile_picture ?? null,
            role: collab?.role || 'collaborator',
          }],
        }) as any);
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removeMatterCollaborator(uuid, collabUuid) as any);
      },
      /*
        PATCH /matters/collaborators/update-access, not the legacy
        POST /matters/update_collaborator_role: the panel identifies people by
        user UUID, and the legacy route does `int(CollaboratorID)` so a UUID
        can only ever 400 there. This is the same action the Collaborators
        section already uses.
      */
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updateCollaboratorAccess(uuid, collabUuid, access as any) as any);
      },
      onUpdate: async () => {
        await refreshSummary(false);
      },
    }));
  }, [dispatch, t, matterId, collaborators, userInfo?.UserUID, refreshSummary, userAccessLevel]);

  /**
   * Export the matter - the real thing, from the server.
   *
   * This used to build a nine-field CSV out of whatever the page happened to
   * hold and never call the backend at all, because at the time there was no
   * endpoint. There is now: `GET /matters/{uuid}/export` returns a ZIP of the
   * matter, so the client-side reconstruction is gone.
   *
   * Fetched through the app's Axios instance with `responseType: 'blob'` —
   * the same mechanism `FileApiBuilder.streamFile` and the research citation
   * downloads use. It has to go through `apis` rather than a bare `fetch` for
   * the Auth0 bearer token, the CSRF header and the license guard; the
   * `responseType` is what stops the interceptor chain treating a zip as JSON
   * and corrupting it.
   *
   * `StreamingService` is deliberately NOT used: it is built around the
   * upload/download-with-SSE-progress file endpoints (domain + resourceId +
   * fileId) and has nowhere to put a one-shot matter-scoped GET. A matter zip
   * is a single modest response, not a streamed upload.
   */
  const [isExporting, setIsExporting] = useState(false);

  const handleExportMatter = useCallback(async () => {
    if (isExporting || !matterId) return;
    setIsExporting(true);
    try {
      const response = await apis.get(`/matters/${matterId}/export`, {
        responseType: 'blob',
      });

      const blob = response.data as Blob;
      // A JSON error body can still arrive with a 200 from some proxies; a
      // zip that is actually an error message is worse than a failure toast.
      if (!blob || blob.size === 0) throw new Error('Empty export');

      // The server names the file; the matter's own name is the fallback.
      const suggested = filenameFromContentDisposition(
        response.headers?.['content-disposition'] || (response.headers as any)?.get?.('content-disposition')
      );
      saveBlob(blob, suggested || withExtension(matterData?.MatterName || `matter-${matterId}`, 'zip'));
    } catch (error) {
      console.error('Failed to export matter:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: t('Could not export this matter'),
        subMsg: t('Please try again in a moment.'),
        duration: 4000,
      }));
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, matterId, matterData?.MatterName, dispatch, t]);

  /**
   * ARCHIVE - the primary destructive action, and the only one here.
   *
   * This is the SOFT DELETE, not the "Archived" rung on the org's status
   * ladder. It used to be the latter: `editMatter({ status_id })`, which left
   * the matter in the active list under a different label and depended on the
   * org having configured such a status at all. Archiving now removes the
   * matter from the active list with everything intact, and the Archive view
   * on the matters list is where it can be restored or, finally, deleted.
   *
   * Afterwards we leave for the list: staying on the page of a matter that is
   * no longer in the active set invites edits to something the user has just
   * put away.
   */
  const handleArchiveMatter = useCallback(() => {
    dispatch(showModal({
      type: 'confirmation',
      title: t('Archive this matter?'),
      message: t('It will be hidden from the active matters list. Nothing is deleted.'),
      subText: t('You can restore it at any time from the Archive.'),
      icon: <PiArchiveLight className="w-6 h-6 text-warning-600" />,
      variant: 'warning',
      confirmLabel: t('Archive'),
      onConfirm: async () => {
        dispatch(hideModal());
        const result = await dispatch(archiveMatter(matterId));
        if (result) history.push('/matters');
      },
      onClose: () => dispatch(hideModal()),
    } as any));
  }, [dispatch, t, matterId, history]);

  // Check if matter has user-uploaded documents (for gating the Analyze button).
  // AI-retrieved laws and cases do NOT count - the user must provide their own context.
  const hasDocuments = useMemo(() => {
    if (!sources) return false;
    const docs = sources.user_documents || sources.documents || sources.files || [];
    return docs.length > 0;
  }, [sources]);

  const matterDropdownActions: Action[] = useMemo(() => {
    const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';

    const analyzing = isAnalyzing || isAnalysisRunning || matterData?.processing_status === 'running';

    return [
      {
        /*
          Re-analyze led the header until it moved in here. It stays FIRST
          because it is still the primary act on a matter - the header just
          stopped being the place for it.
        */
        label: analyzing ? t('Analyzing...') : t('Re-analyze'),
        ...(!hasMatterAi ? { title: t('AI analysis is not included in your plan. Upgrade to unlock.') } : {}),
        onClick: () => {
          if (analyzing || !hasDocuments) return;
          /* Matter AI not on the plan: the upgrade modal, not the wizard. */
          if (!hasMatterAi) { checkAiFeatureGate('matter_ai'); return; }
          openAnalyzeWizard();
        },
        icon: analyzing
          ? <BrandLoader size="xs" className="!h-4 !w-4" />
          : <PiLightningLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
        variant: 'outline' as const,
        // No documents means no context to analyse - the same gate the
        // header button carried.
        disabled: analyzing || !hasDocuments,
      },
      ...(canEdit ? [{
        label: t('Edit details'),
        onClick: () => {
          handleTabClick('Details');
          setIsEditingMatterInfo(true);
        },
        icon: <PiPencilSimpleLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
        variant: 'outline' as const,
      }] : []),
      /* NO "Manage collaborators" HERE. The rail's Access pill (badged with
         the count) raises the pane that lists them, and the pane's own footer
         button is the one way to the editor. A menu item that jumped straight
         to the editor was the third of four routes to the same panel. */
      {
        label: t('Add document'),
        onClick: handleAddDocument,
        icon: <PiFolderSimplePlusLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
        variant: 'outline' as const,
      },
      {
        // The export can take a few seconds on a big matter, so the item says
        // so and refuses a second click rather than looking inert.
        label: isExporting ? t('Preparing export...') : t('Export matter'),
        onClick: handleExportMatter,
        disabled: isExporting,
        icon: isExporting
          ? <Spinner size="glyph" className="!h-4 !w-4" />
          : <PiDownloadSimpleLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
        variant: 'outline' as const,
      },
      /* "Activity log" was removed from this menu - the log already lives as a
         pane on the surface (the Activity pill), so a second entry here was a
         duplicate route to the same place. */
      /*
        Archive is the LAST item and the only destructive one. No longer
        gated on the org having an "Archived" status configured - the soft
        delete is a property of the matter, not of the status ladder.
      */
      ...(canEdit ? [{
        label: t('Archive'),
        onClick: handleArchiveMatter,
        icon: <PiArchiveLight className="w-4 h-4 text-warning-700" />,
        variant: 'destructive' as const,
      }] : []),
    ];
  }, [
    t, userAccessLevel, handleTabClick, handleAddDocument,
    surfaceApi, handleArchiveMatter, handleExportMatter, isExporting,
    isAnalyzing, isAnalysisRunning, matterData?.processing_status, hasDocuments,
    openAnalyzeWizard, hasMatterAi, checkAiFeatureGate,
  ]);


  const additionalHeaderElements = useMemo(() => (
    <div className="flex gap-2 items-center min-w-0 flex-wrap justify-end">
      <ProjectBadges resourceType="matter" resourceUuid={matterId} />
      <MarketplaceOriginBadge matterUuid={matterId} />
      <PendingAccessRequests resourceType="matter" resourceUuid={matterId} />

      {/* Status. Stage is not surfaced anywhere in the UI - the backend
          still tracks it, nothing renders it. */}
      {matterData && (
        <span data-tour-id="matter-status-badges" className="flex items-center gap-2">
          <Badge className="capitalize" tooltip={t('Status')} color={getCurrentStatusColor()}>
            {getCurrentStatusDisplay()}
          </Badge>
        </span>
      )}

      {userType && userType.includes('Law') && matterData?.Clients?.length && !isBetaEnv && (
        <Badge color="green" className="flex items-center gap-1">
          <PiCheckCircleLight className="h-4 w-4" />
          {t('Client has access')}
        </Badge>
      )}

      {/* Context change indicator - next to the primary action for visibility */}
      {!isAnalysisRunning && analysisStatus?.context_changed_since_analysis && analysisStatus?.has_been_analyzed && (
        <Badge color="yellow" icon={<PiWarningLight className="h-3.5 w-3.5" />}>
          {t('Context changed')}
        </Badge>
      )}

      {/* ── Far right: collaborators, the primary action, the overflow ──
          The pane rail is NOT here - it lives on the tabs row, next to the
          navigation it sits beside on screen. */}
      {/* The faces RAISE THE ACCESS PANE. They used to throw the 50%-wide
          manage panel over the page, which meant the only way to look at the
          roster was to open the editor for it. */}
      <CollaboratorStack
        collaborators={collaborators}
        onClick={() => surfaceApi.openPane('access')}
        tourId="matter-collaborators"
        testId="matter-collaborator-stack"
      />

      <Menu as="div" className="relative inline-block text-start z-50" data-tour-id="matter-actions-menu">
        <Menu.Button as={Button} variant="outline" size="small" className="!h-6 !w-6 !p-0 flex items-center justify-center">
          <PiDotsThreeVerticalLight className="h-4 w-4" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-pop duration-150"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="glass absolute end-0 mt-2 w-56 origin-top-right rounded-lg border border-hairline-soft shadow-lg focus:outline-none z-50 p-1">
            {matterDropdownActions.map((action, index) => (
              <Menu.Item key={index}>
                {({ active }) => (
                  <button
                    className={clsx(
                      'group flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm',
                      action.variant === 'destructive' ? 'text-danger-600' : 'text-fg-body',
                      active && (action.variant === 'destructive'
                        ? 'bg-danger-50 dark:bg-danger-900/20'
                        : 'bg-surface-sunken text-fg-strong')
                    )}
                    onClick={action.onClick}
                  >
                    {action.icon}
                    {t(action.label)}
                  </button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  ), [
    collaborators, isAnalysisRunning, analysisStatus, t,
    matterData, matterId, matterDropdownActions, openManageAccess,
    userType, getCurrentStatusColor, getCurrentStatusDisplay,
  ]);

  /**
   * The tab bar.
   *
   * The VALUES come from the surface config (`MATTER_TABS`), so the bar, the
   * URL and `goToTab` share ONE vocabulary and a tab cannot be added to the
   * bar without the surface knowing about it. What is left here is what only
   * the page can supply: the translated label, the icon, the tooltip - and
   * the licence filter, since hiding a pill is a question about this user,
   * not about what a matter's URL may name.
   */
  const tabMeta: Record<MatterTab, Omit<TabItem, 'value'>> = useMemo(
    () => ({
      Details: {
        label: t('Details'),
        icon: <PiFileTextLight className="h-4 w-4" />,
        tooltip: t('Matter information and progress'),
      },
      Documents: {
        label: t('Documents'),
        icon: <PiFileLight className="h-4 w-4" />,
        tooltip: t('Uploaded files and retrieved legal sources'),
      },
      'Q&As': {
        label: t('Q&As'),
        icon: <PiQuestionMarkLight className="h-4 w-4" />,
        tooltip: t('Questionnaire and client questions'),
      },
      Tasks: {
        label: t('Tasks'),
        icon: <PiCheckSquareOffsetLight className="h-4 w-4" />,
        tooltip: t('Suggested and active tasks for this matter'),
      },
      Notes: {
        label: t('Notes'),
        icon: <PiNoteLight className="h-4 w-4" />,
        tooltip: t('Notes and activity history'),
      },
      /* ONE economics tab. Time keeping and Billing rates are sub-tabs
         inside it - see `MatterEconomicsWorkspace`. */
      Economics: {
        label: t('Economics'),
        icon: <PiChartLineUpLight className="h-4 w-4" />,
        tooltip: t('Budget, time keeping and billing rates'),
      },
      /* No Drafts/Strategy tabs here: drafting lives on its own surface
         (`/drafting` → `Drafting/DraftingDetail`), and a matter reaches it
         through its Connected work pane (`surface/MatterConnectedPane`).
         The old in-matter `tabs/Drafts` tree has been removed. */
    }),
    [t]
  );

  const tabs: TabItem[] = useMemo(
    () =>
      MATTER_TABS.filter(
        /* Hidden, not locked, when the module is not licensed - see the note
           on `hasMatterEconomics`. */
        (value) => value !== 'Economics' || (isMatterEconomicsEnabled() && hasMatterEconomics)
      ).map((value) => ({
        value,
        ...tabMeta[value],
      })),
    [tabMeta, hasMatterEconomics]
  );

  if (loadingInitialData) return <MatterDetailsSkeleton />;

  // If we've tried loading and have an error, show error state
  if (dataState.error && dataState.hasTriedLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-medium text-gray-900 dark:text-white mb-2">
            {t('Matter not found')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('The matter you are looking for does not exist or you do not have access to it.')}
          </p>
          <Button
            onClick={() => history.push('/matters')}
            variant='primary'
            size='small'
            className='mx-auto'
          >
            {t('Back to matters')}
          </Button>
        </div>
      </div>
    );
  }

  // If data failed to load and we haven't tried, show skeleton
  if (!matterData && dataState.hasTriedLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-medium text-gray-900 dark:text-white mb-2">
            {t('Matter not found')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('The matter you are looking for does not exist or you do not have access to it.')}
          </p>
          <Button
            onClick={() => history.push('/matters')}
            className="mx-auto"
            variant='primary'
            size='small'
          >
            {t('Back to matters')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <MatterSurfaceProvider value={surfaceApi}>
    <DetailFrame
      breadcrumbs={matterBreadcrumbs}
      dropdownActions={[]}
      subscriptionActive={subscriptionActive}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
    >
      <div className="flex-1 flex flex-col min-h-0">
        {/*
          THE ANALYSIS TAKEOVER STAYS OUTSIDE THE SURFACE.

          While an analysis runs there is no canvas to float panes over - the
          tracker IS the page. Mounting it inside the surface would put a
          progress view behind panes that describe data being rewritten
          underneath them.
        */}
        {isAnalysisRunning ? (
          <div className="flex-1 flex items-center justify-center">
            <ProgressTracker
              steps={analysisProgress}
              isComplete={analysisComplete}
              title={t('Analyzing matter')}
              matterId={matterId}
              correlationId={analysisCorrelationId || undefined}
              isRunning={isAnalysisRunning}
              backendPercentage={analysisPercentage}
            />
          </div>
        ) : (
          <>
          {/*
            THE TABS ARE THEIR OWN ROW, FULL WIDTH, ABOVE EVERYTHING.

            They used to be the first child of the canvas, which put them
            INSIDE the surface - so a docked pane sat over them and the row
            shrank as panes opened. Navigation is not part of the canvas: it
            is the frame around it. Out here the row spans the window, the
            panes float below it, and the strip scrolls horizontally when the
            tab set outgrows a narrow window (see `DetailTabBar`).
          */}
          <div data-tour-id="matter-tab-bar" className="flex-shrink-0 w-full">
            <DetailTabBar
              tabs={tabs}
              activeTab={activeTab}
              onTabClick={handleTabClick}
              showLabels={true}
              tourId="matter-tabs-left"
              /*
                Tabs left, pane toggle right, one row. The rail is passed as
                the bar's `actions` slot, which renders it as a SIBLING of the
                horizontal scroller - so it stays pinned on the right when the
                tabs overflow and scroll underneath it.

                `labels="auto"` drops it to icons below xl, so a narrow window
                shares the row between tabs and toggle instead of clipping.
              */
              actions={(
                <PaneRail
                  panels={panels}
                  ariaLabel={t('Panels')}
                  labels="auto"
                  /* Straight from the surface config: every pane that asked
                     for a rail toggle gets one, in declaration order, so a
                     new pane cannot be added and then forgotten here. */
                  items={MATTER_SURFACE.railItems({
                    t,
                    icons: {
                      assistant: <PiChatsCircleLight />,
                      context: <PiInfoLight />,
                      connected: <PiListMagnifyingGlassLight />,
                      access: <PiUsersLight />,
                      activity: <PiClockCounterClockwiseLight />,
                      aiUsage: <PiSparkleLight />,
                    },
                    // The server's own totals, so a pill is right whether or
                    // not its pane has ever been opened.
                    badges: {
                      connected: connected.work.counts.total,
                      access: collaborators.length,
                    },
                  }).filter((item) => item.id !== 'assistant' || hasResearch)}
                />
              )}
            />
          </div>

          <DetailPageSurface
            surface={MATTER_SURFACE}
            layoutApi={panels}
            panes={([
              {
                id: 'assistant',
                title: t('Assistant'),
                icon: <PiChatsCircleLight />,
                /* Keyed by matter: the pane holds a conversation, and moving
                   between matters must start a new one rather than carry the
                   previous matter's session across. */
                body: (
                  <MatterAssistantPane
                    key={matterId}
                    matterUuid={matterId}
                    /* For the session's jurisdiction + language - the same
                       record, and the same two fields, the Context pane's
                       Overview chips read. */
                    matterData={matterData}
                    work={connected.work}
                    loading={connected.loading}
                    onRefreshConnected={connected.refresh}
                    /* A chat upload the user chose to keep is now a case file,
                       so the Documents tab's list is one row out of date. */
                    onFiledToCaseFiles={refreshSources}
                  />
                ),
              },
              {
                id: 'context',
                title: t('Context'),
                icon: <PiInfoLight />,
                body: (
                  <MatterContextPane
                    matterData={matterData}
                    onUpdate={handleChildUpdate}
                    sources={sources}
                    questionnaire={questionnaire}
                    notes={notes}
                    suggestedTasks={suggestedTasks}
                    matterTasks={matterTasks}
                    statusLabel={getCurrentStatusDisplay()}
                    statusTone={getCurrentStatusColor()}
                  />
                ),
              },
              {
                id: 'connected',
                title: t('Connected'),
                icon: <PiListMagnifyingGlassLight />,
                body: (
                  <MatterConnectedPane
                    matterUuid={matterId}
                    work={connected.work}
                    loading={connected.loading}
                    onRefresh={connected.refresh}
                  />
                ),
              },
              {
                id: 'access',
                title: t('Access'),
                icon: <PiUsersLight />,
                body: (
                  <AccessPane
                    collaborators={collaborators}
                    onManageAccess={openManageAccess}
                    canManage={userAccessLevel === 'edit' || userAccessLevel === 'admin'}
                  />
                ),
              },
              {
                id: 'activity',
                title: t('Activity'),
                icon: <PiClockCounterClockwiseLight />,
                body: (
                  <MatterActivityPane
                    matterId={matterId}
                    matterName={matterData?.MatterName || ''}
                    onClose={() => panels.setVisible('activity', false)}
                  />
                ),
              },
              {
                id: 'aiUsage',
                title: t('AI usage'),
                icon: <PiSparkleLight />,
                /* A thunk: the panel fetches on mount, so a matter nobody
                   asks the cost of never costs a request. */
                body: () => (
                  <AIUsagePanel
                    entityType="matter"
                    entityId={matterId}
                    entityName={matterData?.MatterName || ''}
                    onClose={() => panels.setVisible('aiUsage', false)}
                  />
                ),
              },
              {
                id: 'detail',
                title: detailTitle,
                icon: <PiSquaresFourLight />,
                body: (
                  <MatterItemDetail
                    selection={selection}
                    matterUuid={matterId}
                    canEdit={userAccessLevel === 'edit' || userAccessLevel === 'admin'}
                    notes={notes}
                    questionnaire={questionnaire}
                    matterTasks={matterTasks}
                    suggestions={suggestionEntries}
                    collaborators={collaborators}
                    onUpdate={handleChildUpdate}
                    onDismissSuggestion={removeSuggestion}
                    onClose={() => surfaceApi.clearSelection()}
                  />
                ),
              },
              {
                id: 'reader',
                title: readerContent?.title || t('Reader'),
                icon: <PiBookOpenLight />,
                body: (
                  <MatterReaderPane
                    content={readerContent}
                    onClose={() => panels.setVisible('reader', false)}
                  />
                ),
              },
            ] as const).filter((pane) => pane.id !== 'assistant' || hasResearch) as any}
          >
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
              <MatterDetailsContent
                activeTab={activeTab}
                matterData={matterData}
                userType={userType}
                userAccessLevel={userAccessLevel}
                isEditingMatterInfo={isEditingMatterInfo}
                setIsEditingMatterInfo={setIsEditingMatterInfo}
                editedMatterFields={editedMatterFields}
                setEditedMatterFields={setEditedMatterFields}
                handleSaveMatterInfo={handleSaveMatterInfo}
                onUpdate={handleChildUpdate}
                matterStatuses={matterStatuses}
                sources={sources}
                sourcesLoading={sourcesLoading}
                questionnaire={questionnaire}
                questionnaireLoading={questionnaireLoading}
                notes={notes}
                setNotes={setNotes}
                notesLoading={notesLoading}
                setSources={setSources}
                clients={matterData?.matter_clients?.clients || []}
                suggestedTasks={suggestedTasks}
                setSuggestedTasks={setSuggestedTasks}
                matterTasks={matterTasks}
                setMatterTasks={setMatterTasks}
                suggestedTasksLoading={suggestedTasksLoading}
                matterTasksLoading={matterTasksLoading}
                hasDocuments={hasDocuments}
                onNavigateTab={handleTabClick}
              />
            </div>
          </DetailPageSurface>
          </>
        )}
      </div>

      {/* Matter Detail Orientation Tour */}
      <FeatureTour
        steps={matterDetailOrientationSteps}
        run={isOrientationTourRunning}
        onCallback={handleOrientationTourCallback}
        onBeforeStep={handleOrientationBeforeStep}
        disableOverlay
      />
    </DetailFrame>
    </MatterSurfaceProvider>
  );
};

export default MatterDetails;
